import { useEffect, useState } from 'react'
import { useVariant, useFlagsStatus } from '@unleash/proxy-client-react'
import { trackExperimentViewed as trackExperiment } from 'src/utils/tracking'
import { VariantType } from 'src/types/experiment'

const STABILITY_DELAY = 500

interface Experiment {
  name: string
  experiment: {
    id: string
    name: string
  }
  variantsMap: {
    disabled: string
    control: string
    treatment1: string
    treatment2?: string
  }
  useStabilityDelay?: boolean // when true, it will wait for xms after flagsReady becomes true to ensure values are stable
}

interface Config {
  trackOnMount?: boolean
}

export function useExperiment(
  experiment: Experiment,
  { trackOnMount }: Config = {}
) {
  const { enabled, name, payload } = useVariant(experiment.name)
  const { flagsReady } = useFlagsStatus()
  const [isStable, setIsStable] = useState(!experiment.useStabilityDelay)

  // Add a small delay after flagsReady becomes true to ensure values are stable
  useEffect(() => {
    if (!experiment.useStabilityDelay) {
      setIsStable(true)
      return
    }

    if (flagsReady && !isStable) {
      const timer = setTimeout(() => {
        setIsStable(true)
      }, STABILITY_DELAY) // Small delay to ensure flag values are stable
      return () => clearTimeout(timer)
    } else if (!flagsReady) {
      setIsStable(false)
    }
  }, [flagsReady, isStable, experiment.useStabilityDelay])

  useEffect(() => {
    if (trackOnMount && enabled && name !== VariantType.Disabled) {
      trackExperimentViewed()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackOnMount, enabled, name])

  function trackExperimentViewed(extraData = {}) {
    trackExperiment({
      variation_name: name,
      experiment_name: experiment.experiment.name,
      experiment_id: experiment.experiment.id,
      ...extraData
    })
  }

  return {
    // Whether the experiment is ON or OFF
    enabled,
    variant: name,
    // Whether the experiment is on treatment variant or not
    active:
      (enabled && name === experiment.variantsMap.treatment1) ||
      (enabled && name === experiment.variantsMap.treatment2),
    trackExperimentViewed,
    payload:
      payload?.type === 'json'
        ? { ...payload, value: JSON.parse(payload.value) }
        : payload,
    flagsReady: isStable // Only report ready after the stabilization period
  }
}
